import { STORAGE_KEYS } from '@/services/constant';
import { AES, enc, lib, mode, pad } from 'crypto-js';
import { reactive } from 'vue';

const MASTER_KEY_HEX = process.env.VUE_APP_MASTER_KEY;
const masterKey = enc.Hex.parse(MASTER_KEY_HEX);

const state = reactive({
  encryptionKey: null,
  encryptionIv: null,
  isInitialized: false,
});
const decryptWithPassphrase = (ciphertext, passphrase) => {
  try {
    const bytes = AES.decrypt(ciphertext, passphrase);
    const decrypted = bytes.toString(enc.Utf8);
    return decrypted || null;
  } catch (e) {
    return null;
  }
};

const encryptWithPassphrase = (text, passphrase) => {
  return AES.encrypt(text, passphrase).toString();
};

export const initializeEncryption = async () => {
  if (state.isInitialized) return;

  try {
    const encryptedKey = localStorage.getItem(STORAGE_KEYS.KEY_KEY);
    const encryptedIv = localStorage.getItem(STORAGE_KEYS.IV_KEY);
    const passphrase = MASTER_KEY_HEX;

    if (encryptedKey && encryptedIv) {
      const keyBase64 = decryptWithPassphrase(encryptedKey, passphrase);
      const ivHex = decryptWithPassphrase(encryptedIv, passphrase);
      if (keyBase64 && ivHex) {
        state.encryptionKey = enc.Base64.parse(keyBase64);
        state.encryptionIv = enc.Hex.parse(ivHex);
        state.isInitialized = true;
        return;
      }
    }

    const rawKey = lib.WordArray.random(32);
    const rawIv = lib.WordArray.random(16);

    const keyBase64 = enc.Base64.stringify(rawKey);
    const ivHex = enc.Hex.stringify(rawIv);

    localStorage.setItem(STORAGE_KEYS.KEY_KEY, encryptWithPassphrase(keyBase64, passphrase));
    localStorage.setItem(STORAGE_KEYS.IV_KEY, encryptWithPassphrase(ivHex, passphrase));

    state.encryptionKey = rawKey;
    state.encryptionIv = rawIv;
    state.isInitialized = true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Encryption initialization failed:', error);
    }
    clearSessionKeys();
    throw error;
  }
};

export const clearSessionKeys = () => {
  localStorage.removeItem(STORAGE_KEYS.KEY_KEY);
  localStorage.removeItem(STORAGE_KEYS.IV_KEY);
  state.encryptionKey = null;
  state.encryptionIv = null;
  state.isInitialized = false;
};

export const encryptData = data => {
  if (!state.isInitialized) throw new Error('Encryption not initialized');
  const text = typeof data === 'string' ? data : JSON.stringify(data);
  return AES.encrypt(text, state.encryptionKey, {
    iv: state.encryptionIv,
    padding: pad.Pkcs7,
    mode: mode.CBC,
  }).toString();
};

export const decryptData = encryptedData => {
  if (!state.isInitialized) throw new Error('Encryption not initialized');
  try {
    const bytes = AES.decrypt(encryptedData, state.encryptionKey, {
      iv: state.encryptionIv,
      padding: pad.Pkcs7,
      mode: mode.CBC,
    });
    const decryptedText = bytes.toString(enc.Utf8);
    if (!decryptedText) throw new Error('Decryption failed');
    try {
      return JSON.parse(decryptedText);
    } catch (e) {
      return decryptedText;
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Decryption failed:', error);
    }
    throw error;
  }
};

export const encryptString = message => {
  const iv = lib.WordArray.random(16);
  const encrypted = AES.encrypt(enc.Utf8.parse(message), masterKey, {
    iv: iv,
    padding: pad.Pkcs7,
    mode: mode.CBC,
  });
  return `${encrypted.toString()}:${iv.toString(enc.Base64)}`;
};

export const decryptString = encryptedMessage => {
  try {
    const [ciphertext, iv] = encryptedMessage.split(':');
    const decrypted = AES.decrypt(ciphertext, masterKey, {
      iv: enc.Base64.parse(iv),
      padding: pad.Pkcs7,
      mode: mode.CBC,
    });
    return decrypted.toString(enc.Utf8);
  } catch (error) {
    return null;
  }
};
